import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { openServerPdf, askPdfOrXlsx } from '../utils/pdfPrint';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import SubagentApprovalDialog from './SubagentApprovalDialog';
import { GrantCreditNoteDialog } from './CreditNotesManagement';
import SubsequentAdjustmentsPanel from './SubsequentAdjustmentsPanel';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3 } from 'lucide-react';

const TicketsManagement = (() => {
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  // 🆕 Credit note (exceptional discount after full settlement) target.
  const [creditNoteFor, setCreditNoteFor] = useState(null);
  // === Refund flow state ===
  const [refundTicket, setRefundTicket] = useState(null);     // ticket being refunded
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundImage, setRefundImage] = useState(null);
  const [refundBusy, setRefundBusy] = useState(false);
  
  // Form state (Enhanced with customer information)
  const [ticketForm, setTicketForm] = useState({
    ticket_no: '',
    operation_date: new Date().toISOString().split('T')[0],
    flight_date: new Date().toISOString().split('T')[0],
    carrier_company: '',
    destination: '',
    ticket_price: '',
    commission: '',
    // NEW: Enhanced customer fields
    client_id: 'new_client', // 'new_client' or existing client ID
    client_name: '',
    client_phone: '',
    client_email: '',
    client_cin_passport: '',
    // 🆕 Supplier integration (Phase 1)
    service_origin: 'internal',  // 'internal' = صنهاجة | 'supplier' = مورد
    supplier_id: '',
    supplier_cost: '',
    // Document upload state
    document_file: null
  });

  // 🆕 Suppliers list for the "service origin" picker
  const [suppliers, setSuppliers] = useState([]);

  // Enhanced states for customer management
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showNewClientFields, setShowNewClientFields] = useState(true);
  
  // Document states (Enhanced to support PDF and images)
  const [documentPreview, setDocumentPreview] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('');  // 'image' or 'pdf'
  
  // NEW: Filter states (for managers/accountants)
  const [agencies, setAgencies] = useState([]);
  const [filters, setFilters] = useState({
    agency_id: 'all',
    start_date: '',
    end_date: ''
  });
  const [showReportDialog, setShowReportDialog] = useState(false);
  
  // NEW: Document viewing modal states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalImageTitle, setModalImageTitle] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  // 🆕 "Pending approval only" filter — auto-enabled when arriving from
  // the Dashboard pending-approvals chip. Persisted in component state so
  // the manager can toggle it off after reviewing the queue.
  const [pendingOnly, setPendingOnly] = useState(false);

  // 🆕 Read the navigation intent ONCE on mount so the user lands directly
  // on the pending-approval queue when coming from the Dashboard chip.
  useEffect(() => {
    if (sessionStorage.getItem('pending-intent') === 'tickets') {
      sessionStorage.removeItem('pending-intent');
      setPendingOnly(true);
    }
  }, []);

  // Memoized list: respects the pendingOnly toggle.
  const visibleTickets = pendingOnly
    ? tickets.filter(t => t.status === 'pending_approval')
    : tickets;

  // Fetch tickets data
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query parameters for filtering
      const params = new URLSearchParams();
      if (filters.agency_id && filters.agency_id !== 'all') {
        params.append('agency_id', filters.agency_id);
      }
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      
      const [ticketsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/tickets?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tickets/summary?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTickets(ticketsRes.data);
      setSummary(summaryRes.data);
      
    } catch (error) {
      console.error('Error fetching tickets:', error);
      alert('فشل في تحميل بيانات التذاكر');
    } finally {
      setLoading(false);
    }
  };

  // Fetch agencies for filter
  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  // NEW: Fetch clients for customer selection
  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      alert('فشل في تحميل قائمة العملاء');
    } finally {
      setClientsLoading(false);
    }
  };

  // Handle client selection change
  const handleClientSelectionChange = (clientId) => {
    if (clientId === 'new_client') {
      setShowNewClientFields(true);
      setTicketForm({
        ...ticketForm,
        client_id: 'new_client',
        client_name: '',
        client_phone: '',
        client_email: '',
        client_cin_passport: ''
      });
    } else {
      const selectedClient = clients.find(c => c.id === clientId);
      if (selectedClient) {
        setShowNewClientFields(false);
        setTicketForm({
          ...ticketForm,
          client_id: clientId,
          client_name: selectedClient.name,
          client_phone: selectedClient.phone,
          client_email: '',  // Email not in existing client model
          client_cin_passport: selectedClient.cin_passport
        });
      }
    }
  };

  // NEW: View document function for tickets
  const viewDocument = (documentUrl, documentType, ticketNo) => {
    if (!documentUrl) {
      alert('لا توجد وثيقة لعرضها');
      return;
    }

    const fullUrl = `${API}${documentUrl}`;
    
    if (documentType === 'pdf') {
      // For PDFs, open in new tab/window
      const newWindow = window.open(fullUrl, '_blank');
      if (!newWindow) {
        alert('يرجى السماح للنوافذ المنبثقة لعرض ملف PDF');
      }
    } else {
      // For images, show in modal
      setModalImageUrl(fullUrl);
      setModalImageTitle(`وثيقة التذكرة ${ticketNo}`);
      setShowDocumentModal(true);
    }
  };

  // Handle document file selection (Enhanced)
  const handleDocumentSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setTicketForm({...ticketForm, document_file: null});
      setDocumentPreview(null);
      setDocumentType('');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('نوع الملف غير مدعوم. يجب أن يكون صورة (JPEG, PNG, GIF, WebP) أو ملف PDF');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت');
      return;
    }

    setTicketForm({...ticketForm, document_file: file});
    
    // Set document type
    const docType = file.type === 'application/pdf' ? 'pdf' : 'image';
    setDocumentType(docType);

    // Create preview for images only
    if (docType === 'image') {
      const reader = new FileReader();
      reader.onload = (e) => setDocumentPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setDocumentPreview(null); // No preview for PDF
    }
  };

  // Generate report
  const generateReport = async () => {
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.agency_id && filters.agency_id !== 'all') {
        params.append('agency_id', filters.agency_id);
      }
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      
      const response = await axios.get(`${API}/tickets/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
      setShowReportDialog(true);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('فشل في إنشاء التقرير');
    } finally {
      setReportLoading(false);
    }
  };

  // Print report (FIXED: Authentication issue)
  const printReport = async () => {
    try {
      console.log('Generating printable report...');
      
      const params = {};
      if (filters.agency_id && filters.agency_id !== 'all') params.agency_id = filters.agency_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      // Open in a new tab so the browser shows the report and the user can
      // print it directly. The backend now responds with `inline` disposition.
      askPdfOrXlsx(`${API}/tickets/report/print`, params);
      console.log('✅ Report opened in new tab');

    } catch (error) {
      console.error('Error downloading report:', error);
      alert('❌ فشل في فتح التقرير: ' + error.message);
    }
  };

  // Apply filters
  const applyFilters = () => {
    fetchTickets();
  };

  // Upload document after ticket creation (Enhanced)
  const uploadTicketDocument = async (ticketId, documentFile) => {
    if (!documentFile) return null;

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tickets/${ticketId}/upload-document`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error('فشل في رفع الوثيقة: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingDocument(false);
    }
  };

  // 🆕 2026-07-22: Upload/replace the ORIGINAL supplier ticket document
  // (for side-by-side comparison with the customer's copy).
  const [uploadingOriginal, setUploadingOriginal] = useState({});
  const handleOriginalUpload = async (ticket, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      alert('❌ يُسمح فقط بملفات: JPG / PNG / GIF / WebP / PDF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ حجم الملف يجب أن يكون أقل من 5MB');
      return;
    }
    setUploadingOriginal(prev => ({ ...prev, [ticket.id]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/tickets/${ticket.id}/upload-original-document`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      // Optimistic in-place update so the thumbnail appears instantly
      setTickets(prev => prev.map(t => t.id === ticket.id
        ? { ...t, original_document_url: r.data.original_document_url, original_document_type: r.data.original_document_type }
        : t));
    } catch (e) {
      alert('❌ فشل رفع الوثيقة الأصلية: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingOriginal(prev => ({ ...prev, [ticket.id]: false }));
    }
  };

  const handleOriginalDelete = async (ticket) => {
    if (!window.confirm('هل تريد حذف الوثيقة الأصلية؟ يمكنك رفع أخرى بعد الحذف.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tickets/${ticket.id}/original-document`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(prev => prev.map(t => t.id === ticket.id
        ? { ...t, original_document_url: null, original_document_type: null }
        : t));
    } catch (e) {
      alert('❌ فشل الحذف: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Create ticket (Enhanced with customer validation)
  const createTicket = async () => {
    // Enhanced validation
    if (!ticketForm.ticket_no || !ticketForm.carrier_company || !ticketForm.destination || !ticketForm.ticket_price) {
      alert('يرجى إدخال جميع بيانات التذكرة المطلوبة');
      return;
    }

    if (!ticketForm.client_name) {
      alert('يرجى إدخال اسم العميل');
      return;
    }

    if (!ticketForm.document_file) {
      alert('يرجى رفع وثيقة التذكرة (صورة أو PDF) - هذا مطلوب إجبارياً');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Prepare ticket data with customer information
      const ticketData = {
        ticket_no: ticketForm.ticket_no,
        operation_date: ticketForm.operation_date,
        flight_date: ticketForm.flight_date,
        carrier_company: ticketForm.carrier_company,
        destination: ticketForm.destination,
        ticket_price: parseFloat(ticketForm.ticket_price),
        commission: parseFloat(ticketForm.commission || 0),
        // Enhanced customer fields
        client_id: ticketForm.client_id === 'new_client' ? null : ticketForm.client_id,
        client_name: ticketForm.client_name,
        client_phone: ticketForm.client_phone || null,
        client_email: ticketForm.client_email || null,
        client_cin_passport: ticketForm.client_cin_passport || null,
        // 🆕 Supplier metadata — without these the backend stored the ticket
        // as an "internal" sale, the supplier column rendered empty, the
        // supplier filter excluded it and the financial-entry was missing
        // from the cash-transfer pool.
        service_origin: ticketForm.service_origin || 'internal',
        supplier_id: ticketForm.service_origin === 'supplier' ? (ticketForm.supplier_id || null) : null,
        supplier_cost: ticketForm.service_origin === 'supplier' && ticketForm.supplier_cost !== '' ? parseFloat(ticketForm.supplier_cost) : null,
      };

      const response = await axios.post(`${API}/tickets`, ticketData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const ticketId = response.data.ticket_id;
      
      // Upload document (Required)
      await uploadTicketDocument(ticketId, ticketForm.document_file);

      // 🆕 If this ticket is a supplier resale, register a SALE_DUE in
      // the supplier ledger. Failure here does NOT block the ticket — the
      // admin can re-link from the supplier page later.
      if (ticketForm.service_origin === 'supplier' && ticketForm.supplier_id) {
        const cost = parseFloat(ticketForm.supplier_cost || 0);
        const sell = parseFloat(ticketForm.ticket_price || 0);
        if (cost > 0 && sell > 0) {
          try {
            await axios.post(`${API}/suppliers/_internal/sale-hook`, {
              supplier_id: ticketForm.supplier_id,
              supplier_service_id: null,
              operation_id: ticketId,
              operation_type: 'ticket',
              sell_price: sell,
              cost_price: cost,
              client_name: ticketForm.client_name || null,
              description: `تذكرة ${ticketForm.ticket_no} - ${ticketForm.destination}`,
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (hookErr) {
            console.warn('supplier sale-hook failed:', hookErr.response?.data || hookErr.message);
            alert('⚠️ تم إنشاء التذكرة لكن فشل ربطها بالمورد. راجع كشف حساب المورد لاحقاً.');
          }
        }
      }

      alert('✅ تم إنشاء التذكرة والوصل بنجاح مع معلومات العميل');
      
      // Add new ticket to the list immediately with customer info
      const newTicketData = {
        id: ticketId,
        ticket_no: ticketForm.ticket_no,
        operation_date: ticketForm.operation_date,
        flight_date: ticketForm.flight_date,
        carrier_company: ticketForm.carrier_company,
        destination: ticketForm.destination,
        ticket_price: parseFloat(ticketForm.ticket_price),
        commission: parseFloat(ticketForm.commission || 0),
        // Enhanced customer info
        client_name: ticketForm.client_name,
        client_phone: ticketForm.client_phone,
        client_email: ticketForm.client_email,
        client_cin_passport: ticketForm.client_cin_passport,
        receipt_no: response.data.receipt_no,
        agency_id: user?.agency_id,
        agency_name: user?.agency_name || 'الوكالة',
        created_by_name: user?.name,
        created_at: new Date().toISOString(),
        ticket_document_url: `/uploads/ticket_documents/`, // Will be updated after upload
        document_type: documentType
      };
      
      // Add to bottom of tickets list (newest at bottom as requested)
      setTickets([...tickets, newTicketData]);
      
      // Update summary immediately
      if (summary) {
        setSummary({
          total_tickets: (summary.total_tickets || 0) + 1,
          total_price: (summary.total_price || 0) + parseFloat(ticketForm.ticket_price),
          total_commission: (summary.total_commission || 0) + parseFloat(ticketForm.commission || 0),
          net_amount: (summary.net_amount || 0) + (parseFloat(ticketForm.ticket_price) - parseFloat(ticketForm.commission || 0))
        });
      }
      
      setShowAddDialog(false);
      setTicketForm({
        ticket_no: '',
        operation_date: new Date().toISOString().split('T')[0],
        flight_date: new Date().toISOString().split('T')[0],
        carrier_company: '',
        destination: '',
        ticket_price: '',
        commission: '',
        // Reset enhanced customer fields
        client_id: 'new_client',
        client_name: '',
        client_phone: '',
        client_email: '',
        client_cin_passport: '',
        // 🆕 supplier reset
        service_origin: 'internal',
        supplier_id: '',
        supplier_cost: '',
        document_file: null
      });
      setDocumentPreview(null);
      setShowNewClientFields(true);
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        fetchTickets();
      }, 1000);
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      // 🆕 Surface the smart duplicate warning with an inline confirm
      // dialog so the user can verify before forcing the insert.
      if (
        error.response?.status === 409
        && error.response?.data?.detail?.code === 'DUPLICATE_SUSPECTED'
      ) {
        const det = error.response.data.detail;
        const ex = det.existing || {};
        const human = (
          `${det.message}\n\n` +
          `التذكرة المشابهة:\n` +
          `• الرقم: ${ex.ticket_no || '—'}\n` +
          `• السعر: ${(ex.ticket_price || 0).toLocaleString()} دج\n` +
          `• الوجهة: ${ex.destination || '—'}\n` +
          `• تاريخ الإنشاء: ${ex.created_at ? new Date(ex.created_at).toLocaleString('ar-DZ') : '—'}\n\n` +
          `هل تريد الإكمال رغم ذلك؟`
        );
        if (window.confirm(human)) {
          try {
            const retry = await axios.post(
              `${API}/tickets`,
              { ...ticketData, force: true },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            await uploadTicketDocument(retry.data.ticket_id, ticketForm.document_file);
            alert('✅ تم إنشاء التذكرة (تم تجاوز التحذير)');
            fetchTickets();
            return;
          } catch (retryErr) {
            alert('❌ فشل: ' + (retryErr.response?.data?.detail || retryErr.message));
            return;
          }
        }
        return;
      }
      alert('❌ فشل في إنشاء التذكرة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Edit ticket
  const editTicket = (ticket) => {
    setEditingTicket(ticket);
    setTicketForm({
      ticket_no: ticket.ticket_no,
      operation_date: new Date(ticket.operation_date).toISOString().split('T')[0],
      flight_date: new Date(ticket.flight_date).toISOString().split('T')[0],
      carrier_company: ticket.carrier_company,
      destination: ticket.destination,
      ticket_price: ticket.ticket_price?.toString(),
      commission: ticket.commission?.toString(),
      ticket_image: null // Cannot edit existing image
    });
    setDocumentPreview(null);
    setShowEditDialog(true);
  };

  // Update ticket
  const updateTicket = async () => {
    if (!ticketForm.ticket_no || !ticketForm.carrier_company || !ticketForm.destination || !ticketForm.ticket_price) {
      alert('يرجى إدخال جميع البيانات المطلوبة');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tickets/${editingTicket.id}`, ticketForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تعديل التذكرة والعملية المرتبطة بنجاح');
      setShowEditDialog(false);
      setEditingTicket(null);
      
      // Refresh data
      setTimeout(() => {
        fetchTickets();
      }, 500);
      
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('❌ فشل في تعديل التذكرة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Delete ticket (cascades to daily_operation + payments + supplier
  // ledger SALE_DUE + financial_entry; refused when financially locked).
  const deleteTicket = async (ticket) => {
    if (!confirm(`هل تريد حذف التذكرة ${ticket.ticket_no}؟\n\nسيتم حذف:\n• التذكرة\n• العملية اليومية المرتبطة\n• الدفعات المسجلة\n• قيد دفتر المورد (إن وُجد)\n• قيد التتبع المالي\n\nهذا الإجراء لا يمكن التراجع عنه.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const resp = await axios.delete(`${API}/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const r = resp?.data || {};
      alert(
        `✅ تم حذف التذكرة وكل آثارها\n` +
        `• العملية اليومية: ${r.daily_operation_deleted ? 'تم' : '—'}\n` +
        `• الدفعات: ${r.payments_deleted || 0}\n` +
        `• قيد دفتر المورد: ${r.supplier_ledger_deleted || 0}\n` +
        `• قيد التتبع المالي: ${r.financial_entries_deleted || 0}`
      );

      // Refresh data
      setTimeout(() => {
        fetchTickets();
      }, 500);
      
    } catch (error) {
      console.error('Error deleting ticket:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && typeof detail === 'object' && detail?.code === 'FINANCIAL_LOCK') {
        alert(detail.message || '⛔ لا يمكن الحذف: العملية مرتبطة بدفعة نقدية مغلقة.');
      } else {
        alert('❌ فشل في حذف التذكرة: ' + (typeof detail === 'string' ? detail : error.message));
      }
    }
  };

  // Print ticket receipt
  const printTicketReceipt = (ticket) => {
    try {
      if (ticket.daily_operation_id) {
        askPdfOrXlsx(`${API}/daily-operations/${ticket.daily_operation_id}/print`);
      } else {
        alert('لا يمكن طباعة الوصل - لا توجد عملية مرتبطة');
      }
    } catch (error) {
      console.error('Error printing ticket receipt:', error);
      alert('فشل في طباعة الوصل');
    }
  };

  /** Open refund modal for a ticket. */
  const openRefundDialog = (ticket) => {
    setRefundTicket(ticket);
    setRefundAmount(String(ticket.ticket_price || ''));
    setRefundReason('');
    setRefundImage(null);
  };

  /**
   * Submit refund: requires amount + reason + image. Backend creates the
   * negative daily_operation, marks the ticket "refunded", stores proof.
   */
  const submitRefund = async () => {
    if (!refundTicket) return;
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { alert('⚠️ المبلغ يجب أن يكون أكبر من 0'); return; }
    if (!refundReason || refundReason.trim().length < 5) {
      alert('⚠️ السبب يجب أن يكون 5 أحرف على الأقل');
      return;
    }
    if (!refundImage) {
      alert('⚠️ صورة الوثيقة إجبارية (ملف PDF غير مقبول لأنه يُحذف بعد فترة)');
      return;
    }
    setRefundBusy(true);
    try {
      const fd = new FormData();
      fd.append('amount', String(amt));
      fd.append('reason', refundReason.trim());
      fd.append('image', refundImage);
      const r = await axios.post(`${API}/tickets/${refundTicket.id}/refund`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      // 🆕 Non-admin path returns status='pending_approval' instead of executing.
      if (r.data?.status === 'pending_approval') {
        alert(
          `📤 ${r.data.message || 'تم إرسال طلب الاسترجاع للموافقة'}\n` +
          `يمكنك متابعة حالة طلبك في "📋 طلباتي" بالقائمة الجانبية.`
        );
      } else {
        alert(`${r.data.message}\nرقم وصل الاسترجاع: ${r.data.refund_operation_no}`);
      }
      setRefundTicket(null);
      fetchTickets();
    } catch (e) {
      alert('❌ فشل الاسترجاع: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRefundBusy(false);
    }
  };

  // ============================================================
  // 🆕 Ticket approval flow — only general_accountant / super_admin
  // can approve or reject, and never their own entries (option 3A).
  // Approve flips the ticket + its daily_operations mirror to APPROVED,
  // so revenue starts counting toward the agency balance.
  // Reject deletes the mirror + payment and marks the ticket
  // "rejected" with a reason, so staff sees why & can recreate (option 4C).
  // ============================================================
  const canApprove = user?.role === 'general_accountant' || user?.role === 'super_admin';

  const approveTicket = async (ticket) => {
    if (!canApprove) return;
    if (ticket.created_by === user?.id) {
      alert('⛔ لا يمكنك اعتماد تذكرة أنشأتها بنفسك. يجب أن يعتمدها شخص آخر.');
      return;
    }
    // 🤝 Subagent flow — open the supplier-picker dialog instead of approving directly.
    const agency = agencies.find(a => a.id === ticket.agency_id);
    if (agency?.is_subagent) {
      setSubagentApprovalTarget({
        ticket,
        itemLabel: `تذكرة ${ticket.ticket_no || ''}`,
        agencyName: agency?.name,
        sellPrice: ticket.ticket_price,
      });
      return;
    }
    if (!window.confirm(`✅ اعتماد التذكرة ${ticket.ticket_no || ''}؟\nسيصبح المبلغ مرئياً في الحالة المالية للوكالة.`)) return;
    try {
      await axios.post(
        `${API}/tickets/${ticket.id}/approve`, {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      fetchTickets();
    } catch (e) {
      alert('❌ فشل الاعتماد: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // 🤝 Subagent approval dialog state + handler
  const [subagentApprovalTarget, setSubagentApprovalTarget] = useState(null);
  const [subagentApprovalBusy, setSubagentApprovalBusy] = useState(false);
  const handleSubagentApprovalConfirm = async (payload) => {
    if (!subagentApprovalTarget) return;
    setSubagentApprovalBusy(true);
    try {
      await axios.post(
        `${API}/tickets/${subagentApprovalTarget.ticket.id}/approve`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setSubagentApprovalTarget(null);
      fetchTickets();
    } catch (e) {
      alert('❌ فشل الاعتماد: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubagentApprovalBusy(false);
    }
  };

  const rejectTicket = async (ticket) => {
    if (!canApprove) return;
    if (ticket.created_by === user?.id) {
      alert('⛔ لا يمكنك رفض تذكرة أنشأتها بنفسك.');
      return;
    }
    const reason = window.prompt('سبب الرفض (يُعاد للموظف ليصلّحها):');
    if (reason === null) return; // user cancelled
    if (reason.trim().length < 3) {
      alert('⚠️ يرجى كتابة سبب واضح (3 أحرف على الأقل)');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('reason', reason.trim());
      await axios.post(
        `${API}/tickets/${ticket.id}/reject`, fd,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      fetchTickets();
    } catch (e) {
      alert('❌ فشل الرفض: ' + (e?.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchClients(); // Fetch clients for customer selection
    
    // Fetch agencies for filter (managers/accountants only)
    if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
      fetchAgencies();
    }
    // 🆕 Load active suppliers for the origin picker
    (async () => {
      try {
        const r = await axios.get(`${API}/suppliers?status=active`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setSuppliers(r.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">✈️ إدارة التذاكر</h1>
          <p className="text-sm text-gray-600 mt-1">إدارة تذاكر الطيران مع الربط التلقائي بالعمليات اليومية</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-green-600 hover:bg-green-700">
          ➕ إضافة تذكرة جديدة
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-600">
                  {summary.total_tickets || 0}
                </div>
                <div className="text-sm text-gray-600">إجمالي التذاكر</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {summary.total_price?.toLocaleString() || 0}
                </div>
                <div className="text-sm text-gray-600">مجموع أسعار التذاكر (دج)</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {summary.total_commission?.toLocaleString() || 0}
                </div>
                <div className="text-sm text-gray-600">مجموع العمولات (دج)</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {summary.net_amount?.toLocaleString() || 0}
                </div>
                <div className="text-sm text-gray-600">صافي المبلغ (دج)</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters Section (للمحاسب والمدير فقط) */}
      {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
        <Card>
          <CardHeader>
            <CardTitle>🔍 فلاتر التذاكر والتقارير</CardTitle>
            <CardDescription>
              فلترة التذاكر حسب الوكالة والتاريخ مع إمكانية إنشاء تقارير
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Agency Filter */}
              <div>
                <Label>الوكالة</Label>
                <select
                  value={filters.agency_id}
                  onChange={(e) => setFilters({...filters, agency_id: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="all">جميع الوكالات</option>
                  {agencies.map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name} - {agency.city}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Start Date Filter */}
              <div>
                <Label>من تاريخ</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>
              
              {/* End Date Filter */}
              <div>
                <Label>إلى تاريخ</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-end space-x-2">
                <Button
                  onClick={applyFilters}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  🔍 تطبيق الفلتر
                </Button>
                <Button
                  onClick={generateReport}
                  disabled={reportLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {reportLoading ? 'جاري...' : '📊 تقرير'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>📋 جدول التذاكر</CardTitle>
          <CardDescription>
            جميع تذاكر الطيران مع الربط التلقائي بالعمليات اليومية
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 🆕 Pending-only quick filter — auto-on when arriving from the
              dashboard chip, freely toggleable afterwards. */}
          {tickets.length > 0 && (() => {
            const pendingCount = tickets.filter(t => t.status === 'pending_approval').length;
            return (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-2.5 rounded-lg border bg-amber-50/40">
                <div className="text-sm font-semibold text-gray-700">
                  {pendingOnly
                    ? `🟡 يتم عرض التذاكر التي بانتظار الاعتماد فقط (${visibleTickets.length}/${tickets.length})`
                    : `📋 عرض كل التذاكر (${tickets.length})${pendingCount > 0 ? ` — ${pendingCount} بانتظار الاعتماد` : ''}`}
                </div>
                <button
                  onClick={() => setPendingOnly(p => !p)}
                  className={
                    pendingOnly
                      ? 'bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs font-bold transition'
                      : 'bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-xs font-bold transition'
                  }
                  data-testid="tickets-pending-only-toggle"
                >
                  {pendingOnly ? '🔄 عرض الكل' : '🟡 عرض المعلَّقة فقط'}
                </button>
              </div>
            );
          })()}
          {tickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>لا توجد تذاكر مسجلة حالياً</p>
              <p className="text-sm">اضغط "إضافة تذكرة جديدة" لبدء التسجيل</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">👤 الموظف</TableHead>
                    <TableHead className="text-right">اسم العميل</TableHead>
                    <TableHead className="text-right">هاتف العميل</TableHead>
                    <TableHead className="text-right">تاريخ العملية</TableHead>
                    <TableHead className="text-right">تاريخ الرحلة</TableHead>
                    <TableHead className="text-right">الشركة الناقلة</TableHead>
                    <TableHead className="text-right">الوجهة</TableHead>
                    <TableHead className="text-right">رقم التذكرة</TableHead>
                    <TableHead className="text-right">ثمن التذكرة الإجمالي (دج)</TableHead>
                    <TableHead className="text-right">عمولة الوكالة المناولة (دج)</TableHead>
                    <TableHead className="text-right">📦 المورد</TableHead>
                    <TableHead className="text-right">💰 تكلفة المورد</TableHead>
                    <TableHead className="text-right">📈 الربح</TableHead>
                    <TableHead className="text-right">رقم الوصل</TableHead>
                    <TableHead className="text-right">📄 الوثيقة</TableHead>
                    <TableHead className="text-right">🗂️ الوثيقة الأصلية</TableHead>
                    <TableHead className="text-right">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleTickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      {/* 👤 Employee who created the ticket */}
                      <TableCell className="text-right text-sm font-medium text-amber-700">
                        {ticket.created_by_name || '—'}
                      </TableCell>
                      {/* Customer Information */}
                      <TableCell className="text-right font-medium text-brand-600">
                        {ticket.client_name || 'غير محدد'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-gray-600">
                        {ticket.client_phone || '-'}
                      </TableCell>
                      
                      <TableCell className="text-right">
                        <div>{new Date(ticket.operation_date).toLocaleDateString('ar-DZ')}</div>
                        {ticket.created_at && (
                          <div className="text-xs text-gray-500" dir="ltr">
                            🕐 {formatTimeWithEnglishNumerals(ticket.created_at)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {new Date(ticket.flight_date).toLocaleDateString('ar-DZ')}
                      </TableCell>
                      <TableCell className="text-right">{ticket.carrier_company}</TableCell>
                      <TableCell className="text-right">{ticket.destination}</TableCell>
                      <TableCell className="text-right font-medium text-brand-600">
                        {ticket.ticket_no}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        {ticket.ticket_price?.toLocaleString()} دج
                      </TableCell>
                      <TableCell className="text-right font-bold text-purple-600">
                        {ticket.commission?.toLocaleString()} دج
                      </TableCell>

                      {/* 🆕 Supplier (transparency for the whole team) */}
                      <TableCell className="text-right" data-testid={`ticket-row-supplier-${ticket.id}`}>
                        {ticket.supplier_id ? (
                          <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold" title={ticket.supplier_name || ticket.supplier_id}>
                            📦 {ticket.supplier_name || '—'}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">— داخلي —</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right" data-testid={`ticket-row-cost-${ticket.id}`}>
                        {ticket.supplier_cost != null && ticket.supplier_cost > 0 ? (
                          <span className="text-red-700 font-semibold">{Number(ticket.supplier_cost).toLocaleString()} دج</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right" data-testid={`ticket-row-profit-${ticket.id}`}>
                        {ticket.supplier_cost != null && ticket.supplier_cost > 0 ? (
                          (() => {
                            const profit = (ticket.ticket_price || 0) - ticket.supplier_cost;
                            return (
                              <span className={profit >= 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
                                {profit >= 0 ? '+' : ''}{Number(profit).toLocaleString()} دج
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>

                      <TableCell className="text-right font-medium text-orange-600">
                        {ticket.receipt_no}
                      </TableCell>
                      
                      {/* Enhanced Document Display with Click to View */}
                      <TableCell className="text-right">
                        {ticket.ticket_document_url ? (
                          <div className="flex items-center justify-center">
                            {ticket.document_type === 'pdf' ? (
                              <div 
                                className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center cursor-pointer border border-red-200 hover:bg-red-100 transition-colors"
                                onClick={() => viewDocument(ticket.ticket_document_url, 'pdf', ticket.ticket_no)}
                                title="انقر لمشاهدة ملف PDF"
                              >
                                <span className="text-red-600 text-xs text-center">
                                  📄<br/>PDF
                                </span>
                              </div>
                            ) : (
                              <img 
                                src={`${API}${ticket.ticket_document_url}`}
                                alt="وثيقة التذكرة"
                                className="w-16 h-16 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => viewDocument(ticket.ticket_document_url, 'image', ticket.ticket_no)}
                                title="انقر لمشاهدة الصورة"
                              />
                            )}
                          </div>
                        ) : ticket.ticket_image_url ? (
                          // Fallback to old field for backward compatibility
                          <img 
                            src={`${API}${ticket.ticket_image_url}`}
                            alt="صورة التذكرة"
                            className="w-16 h-16 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => viewDocument(ticket.ticket_image_url, 'image', ticket.ticket_no)}
                            title="انقر لمشاهدة الصورة"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <span className="text-gray-400 text-xs text-center">لا توجد<br/>وثيقة</span>
                          </div>
                        )}
                      </TableCell>

                      {/* 🆕 2026-07-22: Original supplier ticket document
                          (comparison with the customer copy). Uploaded via a
                          hidden <input type="file"> triggered by the icon. */}
                      <TableCell className="text-right" data-testid={`ticket-original-cell-${ticket.id}`}>
                        <div className="flex flex-col items-center gap-1">
                          {ticket.original_document_url ? (
                            <>
                              {ticket.original_document_type === 'pdf' ? (
                                <div
                                  className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center cursor-pointer border border-purple-200 hover:bg-purple-100 transition-colors"
                                  onClick={() => viewDocument(ticket.original_document_url, 'pdf', `${ticket.ticket_no} - أصلية`)}
                                  title="انقر لمشاهدة الوثيقة الأصلية"
                                  data-testid={`ticket-original-view-${ticket.id}`}
                                >
                                  <span className="text-purple-700 text-xs text-center font-bold">
                                    🗂️<br/>PDF
                                  </span>
                                </div>
                              ) : (
                                <img
                                  src={`${API}${ticket.original_document_url}`}
                                  alt="الوثيقة الأصلية"
                                  className="w-16 h-16 object-cover rounded-lg border border-purple-300 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => viewDocument(ticket.original_document_url, 'image', `${ticket.ticket_no} - أصلية`)}
                                  title="انقر لمشاهدة الوثيقة الأصلية"
                                  data-testid={`ticket-original-view-${ticket.id}`}
                                />
                              )}
                              <div className="flex items-center gap-1">
                                <label
                                  className="text-[10px] px-2 py-0.5 rounded bg-purple-100 hover:bg-purple-200 text-purple-800 cursor-pointer border border-purple-300"
                                  title="استبدال الوثيقة الأصلية"
                                >
                                  🔁 استبدال
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) handleOriginalUpload(ticket, f);
                                      e.target.value = '';
                                    }}
                                    data-testid={`ticket-original-replace-input-${ticket.id}`}
                                  />
                                </label>
                                <button
                                  onClick={() => handleOriginalDelete(ticket)}
                                  className="text-[10px] px-2 py-0.5 rounded bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
                                  title="حذف الوثيقة الأصلية"
                                  data-testid={`ticket-original-delete-${ticket.id}`}
                                >
                                  🗑️
                                </button>
                              </div>
                            </>
                          ) : (
                            <label
                              className="w-16 h-16 rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 hover:bg-purple-100 flex flex-col items-center justify-center cursor-pointer transition-colors"
                              title="اضغط لرفع الوثيقة الأصلية (JPG / PNG / PDF)"
                              data-testid={`ticket-original-upload-label-${ticket.id}`}
                            >
                              {uploadingOriginal[ticket.id] ? (
                                <span className="text-[10px] text-purple-700 text-center">⏳<br/>جارٍ الرفع</span>
                              ) : (
                                <span className="text-[10px] text-purple-700 text-center font-bold">
                                  ⬆️<br/>رفع أصلية
                                </span>
                              )}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleOriginalUpload(ticket, f);
                                  e.target.value = '';
                                }}
                                disabled={!!uploadingOriginal[ticket.id]}
                                data-testid={`ticket-original-upload-input-${ticket.id}`}
                              />
                            </label>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-2 flex-wrap">
                          {/* 🆕 Approval status badge — visible to everyone. */}
                          {ticket.status === 'pending_approval' && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300 whitespace-nowrap" data-testid={`badge-pending-${ticket.id}`}>
                              ⏳ بانتظار الاعتماد
                            </Badge>
                          )}
                          {ticket.status === 'rejected' && (
                            <Badge
                              className="bg-red-100 text-red-800 border-red-300 whitespace-nowrap cursor-help"
                              title={ticket.rejection_reason ? `سبب الرفض: ${ticket.rejection_reason}` : 'مرفوضة'}
                              data-testid={`badge-rejected-${ticket.id}`}
                            >
                              ❌ مرفوضة
                            </Badge>
                          )}

                          {/* 🆕 Approve / Reject — only for accountant/admin
                              and only on pending tickets they didn't create. */}
                          {canApprove && ticket.status === 'pending_approval' && ticket.created_by !== user?.id && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => approveTicket(ticket)}
                                className="text-green-700 border-green-400 hover:bg-green-50 font-bold"
                                data-testid={`approve-ticket-${ticket.id}`}
                              >
                                ✅ اعتماد
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => rejectTicket(ticket)}
                                className="text-red-700 border-red-400 hover:bg-red-50 font-bold"
                                data-testid={`reject-ticket-${ticket.id}`}
                              >
                                ❌ رفض
                              </Button>
                            </>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => printTicketReceipt(ticket)}
                            className="text-brand-600 hover:text-brand-700"
                          >
                            🖨️ طباعة الوصل
                          </Button>

                          {/* === Refund button (visible only on active tickets) === */}
                          {ticket.status !== 'refunded' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRefundDialog(ticket)}
                              className="text-orange-700 border-orange-400 hover:bg-orange-50 font-bold"
                              title="استرجاع التذكرة (سينشئ عملية مالية سالبة في الصندوق)"
                              data-testid={`refund-ticket-${ticket.id}`}
                            >
                              💸 استرجاع
                            </Button>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full font-bold whitespace-nowrap">
                              🔄 مُسترَجَع
                              {ticket.refund_operation_no && (
                                <span className="font-mono mr-1 text-[10px]">
                                  ({ticket.refund_operation_no})
                                </span>
                              )}
                            </span>
                          )}
                          
                          {/* Edit Button (General Accountant and Super Admin only) */}
                          {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => editTicket(ticket)}
                              className="text-green-600 hover:text-green-700"
                            >
                              ✏️ تعديل
                            </Button>
                          )}
                          
                          {/* Delete Button (General Accountant and Super Admin only) */}
                          {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteTicket(ticket)}
                              className="text-red-600 hover:text-red-700"
                            >
                              🗑️ حذف
                            </Button>
                          )}
                          {/* 🆕 Grant exceptional discount — fully paid tickets only.
                              Open to ALL roles (employees get pending_approval; admin auto-approve). */}
                          {((ticket.sell_price || 0) - (ticket.amount_paid || 0)) <= 0.01
                            && (ticket.sell_price || 0) > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCreditNoteFor({
                                id: ticket.id,
                                label: ticket.ticket_no,
                              })}
                              className="text-amber-700 hover:text-amber-800 border-amber-300"
                              data-testid={`grant-cn-ticket-${ticket.id}`}
                              title="منح تخفيض استثنائي بعد التسوية الكاملة"
                            >
                              💸
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Ticket Dialog - Enhanced Horizontal Layout */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[1000px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>➕ إضافة تذكرة جديدة</DialogTitle>
            <DialogDescription>
              إنشاء تذكرة جديدة مع ربط تلقائي بالعمليات اليومية
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Main Form - Horizontal Layout */}
            <div className="grid grid-cols-3 gap-6">
              
              {/* Column 1: Ticket Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-brand-800 border-b pb-1">📄 بيانات التذكرة</h3>
                
                <div>
                  <Label>رقم التذكرة *</Label>
                  <Input
                    value={ticketForm.ticket_no}
                    onChange={(e) => setTicketForm({...ticketForm, ticket_no: e.target.value})}
                    placeholder="رقم التذكرة"
                  />
                </div>
                
                <div>
                  <Label>الشركة الناقلة *</Label>
                  <Input
                    value={ticketForm.carrier_company}
                    onChange={(e) => setTicketForm({...ticketForm, carrier_company: e.target.value})}
                    placeholder="اسم شركة الطيران"
                  />
                </div>
                
                <div>
                  <Label>الوجهة *</Label>
                  <Input
                    value={ticketForm.destination}
                    onChange={(e) => setTicketForm({...ticketForm, destination: e.target.value})}
                    placeholder="وجهة الرحلة"
                  />
                </div>

                <div>
                  <Label>تاريخ العملية *</Label>
                  <Input
                    type="date"
                    value={ticketForm.operation_date}
                    onChange={(e) => setTicketForm({...ticketForm, operation_date: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>تاريخ الرحلة *</Label>
                  <Input
                    type="date"
                    value={ticketForm.flight_date}
                    onChange={(e) => setTicketForm({...ticketForm, flight_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>ثمن التذكرة الإجمالي (سعر التذكرة + جميع العمولات) (دج) *</Label>
                  <Input
                    type="number"
                    value={ticketForm.ticket_price}
                    onChange={(e) => setTicketForm({...ticketForm, ticket_price: e.target.value})}
                    placeholder="السعر الإجمالي شاملاً كل العمولات"
                  />
                </div>
                
                <div>
                  <Label>العمولة للوكالات المناولة (دج)</Label>
                  <Input
                    type="number"
                    value={ticketForm.commission}
                    onChange={(e) => setTicketForm({...ticketForm, commission: e.target.value})}
                    placeholder="عمولة الوكالة المناولة (اختياري)"
                  />
                </div>

                {/* 🆕 Auto-computed profit display — bright purple card
                    so the employee can see at a glance how much the
                    agency is making on this ticket. */}
                {(ticketForm.ticket_price || ticketForm.commission) && (
                  <div className="md:col-span-2 bg-gradient-to-r from-fuchsia-50 to-purple-100 border-2 border-fuchsia-300 rounded-lg p-3" data-testid="ticket-profit-display">
                    <div className="flex items-center justify-between">
                      <span className="text-fuchsia-900 font-bold text-sm">💎 الربح الصافي للوكالة:</span>
                      <span className="text-2xl font-extrabold text-fuchsia-700">
                        {(() => {
                          const sell = parseFloat(ticketForm.ticket_price || 0);
                          const comm = parseFloat(ticketForm.commission || 0);
                          const cost = parseFloat(ticketForm.supplier_cost || 0);
                          // profit = sell - commission_paid_out - supplier_cost
                          const profit = sell - comm - cost;
                          return profit.toLocaleString() + ' دج';
                        })()}
                      </span>
                    </div>
                    <p className="text-xs text-fuchsia-700 mt-1">
                      💡 المعادلة: الثمن الإجمالي − عمولة الوكالة المناولة − تكلفة المورد
                    </p>
                  </div>
                )}

                {/* 🆕 Supplier integration block (in CREATE dialog) — hidden for subagent users */}
                {!user?.is_subagent && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-3" data-testid="ticket-origin-block">
                  <Label className="text-sm font-bold text-yellow-800">📦 مصدر الخدمة</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTicketForm({ ...ticketForm, service_origin: 'internal', supplier_id: '', supplier_cost: '' })}
                      className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${ticketForm.service_origin === 'internal' ? 'bg-green-100 border-green-400 text-green-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                      data-testid="ticket-origin-internal-btn"
                    >🏢 خدمة صنهاجة</button>
                    <button
                      type="button"
                      onClick={() => setTicketForm({ ...ticketForm, service_origin: 'supplier' })}
                      className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${ticketForm.service_origin === 'supplier' ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                      data-testid="ticket-origin-supplier-btn"
                    >📦 خدمة مورد</button>
                  </div>
                  {ticketForm.service_origin === 'supplier' && (
                    <div className="space-y-2 pt-1">
                      <div>
                        <Label className="text-xs">المورد *</Label>
                        <select
                          value={ticketForm.supplier_id}
                          onChange={e => setTicketForm({ ...ticketForm, supplier_id: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                          data-testid="ticket-supplier-select"
                        >
                          <option value="">اختر مورداً...</option>
                          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        {suppliers.length === 0 && (
                          <p className="text-xs text-orange-600 mt-1">⚠️ لا يوجد موردون. أضف مورداً أولاً من "إدارة الموردين".</p>
                        )}
                      </div>
                      {/* 🆕 Transparency: supplier cost is visible to
                          every employee so the team can audit each other. */}
                      <div>
                        <Label className="text-xs">تكلفة المورد (دج) *</Label>
                        <Input type="number" value={ticketForm.supplier_cost} onChange={e => setTicketForm({ ...ticketForm, supplier_cost: e.target.value })} placeholder="ما تدفعه الوكالة للمورد" data-testid="ticket-supplier-cost" />
                        {ticketForm.supplier_cost && ticketForm.ticket_price && (
                          <p className="text-xs mt-1 text-green-700">
                            💰 الربح: {(parseFloat(ticketForm.ticket_price || 0) - parseFloat(ticketForm.supplier_cost || 0)).toLocaleString()} دج
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}

              </div>

              {/* Column 2: Customer Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-green-800 border-b pb-1">👤 معلومات العميل</h3>
                
                <div>
                  <Label className="text-sm font-medium">اختيار العميل *</Label>
                  <SearchableSelect
                    value={ticketForm.client_id}
                    onChange={(value) => handleClientSelectionChange(value || 'new_client')}
                    options={[
                      { value: 'new_client', label: '➕ عميل جديد', search: 'جديد new add' },
                      ...clients.map(c => ({
                        value: c.id,
                        label: c.name,
                        hint: c.phone || c.email || '',
                        search: `${c.name} ${c.phone || ''} ${c.email || ''}`,
                      })),
                    ]}
                    placeholder={clientsLoading ? 'جاري تحميل العملاء...' : 'اختر العميل أو ابحث عنه...'}
                    searchPlaceholder="🔎 ابحث بالاسم أو الهاتف..."
                    emptyText="لا يوجد عميل مطابق — اختر '➕ عميل جديد'"
                    allowClear={false}
                    testId="ticket-client-select"
                  />
                  {clientsLoading && (
                    <p className="text-xs text-green-600 mt-1">جاري تحميل العملاء...</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm">اسم العميل *</Label>
                  <Input
                    value={ticketForm.client_name}
                    onChange={(e) => setTicketForm({...ticketForm, client_name: e.target.value})}
                    placeholder="الاسم الكامل للعميل"
                    disabled={!showNewClientFields}
                    className={showNewClientFields ? "" : "bg-gray-100"}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">رقم الهاتف</Label>
                  <Input
                    value={ticketForm.client_phone}
                    onChange={(e) => setTicketForm({...ticketForm, client_phone: e.target.value})}
                    placeholder="رقم الهاتف"
                    disabled={!showNewClientFields}
                    className={showNewClientFields ? "" : "bg-gray-100"}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">البريد الإلكتروني</Label>
                  <Input
                    type="email"
                    value={ticketForm.client_email}
                    onChange={(e) => setTicketForm({...ticketForm, client_email: e.target.value})}
                    placeholder="البريد الإلكتروني (اختياري)"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">رقم الهوية/جواز السفر</Label>
                  <Input
                    value={ticketForm.client_cin_passport}
                    onChange={(e) => setTicketForm({...ticketForm, client_cin_passport: e.target.value})}
                    placeholder="رقم الهوية أو جواز السفر"
                    disabled={!showNewClientFields}
                    className={showNewClientFields ? "" : "bg-gray-100"}
                  />
                </div>
                
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-xs text-green-700">
                    💡 يمكنك اختيار عميل موجود من القائمة أو إدخال معلومات عميل جديد
                  </p>
                </div>
              </div>

              {/* Column 3: Document Upload & Preview */}
              <div className="space-y-4">
                <h3 className="font-medium text-orange-800 border-b pb-1">📄 الوثيقة المطلوبة</h3>
                
                <div>
                  <Label className="font-medium text-orange-800 mb-2 block">رفع وثيقة التذكرة *</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleDocumentSelect}
                    className="cursor-pointer"
                    required
                  />
                  <div className="text-xs text-orange-700 mt-2 space-y-1">
                    <p>📷 الملفات المدعومة: صور أو PDF</p>
                    <p>📐 الحد الأقصى: 5 ميجابايت</p>
                    <p className="font-semibold text-red-600">⚠️ رفع الوثيقة إجباري</p>
                  </div>
                </div>
                
                {/* Document Preview */}
                {documentPreview && documentType === 'image' && (
                  <div>
                    <Label className="text-sm text-gray-700 mb-2 block">معاينة الوثيقة:</Label>
                    <div className="border border-gray-300 rounded-lg p-2 bg-white">
                      <img 
                        src={documentPreview} 
                        alt="معاينة التذكرة" 
                        className="w-full max-h-48 object-contain rounded"
                      />
                    </div>
                  </div>
                )}
                
                {ticketForm.document_file && documentType === 'pdf' && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-red-600 text-lg">📄</span>
                      <span className="text-sm text-red-800">
                        PDF: {ticketForm.document_file.name}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      سيتم رفع الملف بعد إنشاء التذكرة
                    </p>
                  </div>
                )}

                {/* Price Preview */}
                {ticketForm.ticket_price && (
                  <div className="bg-brand-50 p-3 rounded-lg mt-4">
                    <h4 className="font-bold text-brand-800 mb-2">معاينة المبالغ:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>ثمن التذكرة الإجمالي:</span>
                        <span className="font-bold text-green-600">{parseFloat(ticketForm.ticket_price || 0).toLocaleString()} دج</span>
                      </div>
                      <div className="flex justify-between">
                        <span>عمولة الوكالة المناولة:</span>
                        <span className="font-bold text-purple-600">{parseFloat(ticketForm.commission || 0).toLocaleString()} دج</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span>صافي المبلغ:</span>
                        <span className="font-bold text-brand-600">
                          {(parseFloat(ticketForm.ticket_price || 0) - parseFloat(ticketForm.commission || 0)).toLocaleString()} دج
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 border-t pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                إلغاء
              </Button>
              <Button onClick={createTicket} className="bg-green-600 hover:bg-green-700">
                ✈️ إنشاء التذكرة والوصل
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Ticket Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>✏️ تعديل التذكرة</DialogTitle>
            <DialogDescription>
              تعديل بيانات التذكرة - سيتم تحديث العملية المرتبطة تلقائياً
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>رقم التذكرة *</Label>
                <Input
                  value={ticketForm.ticket_no}
                  onChange={(e) => setTicketForm({...ticketForm, ticket_no: e.target.value})}
                  placeholder="رقم التذكرة"
                />
              </div>
              
              <div>
                <Label>تاريخ العملية *</Label>
                <Input
                  type="date"
                  value={ticketForm.operation_date}
                  onChange={(e) => setTicketForm({...ticketForm, operation_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>تاريخ الرحلة *</Label>
                <Input
                  type="date"
                  value={ticketForm.flight_date}
                  onChange={(e) => setTicketForm({...ticketForm, flight_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>الشركة الناقلة *</Label>
                <Input
                  value={ticketForm.carrier_company}
                  onChange={(e) => setTicketForm({...ticketForm, carrier_company: e.target.value})}
                  placeholder="اسم شركة الطيران"
                />
              </div>
              
              <div>
                <Label>الوجهة *</Label>
                <Input
                  value={ticketForm.destination}
                  onChange={(e) => setTicketForm({...ticketForm, destination: e.target.value})}
                  placeholder="وجهة الرحلة"
                />
              </div>
              
              <div>
                <Label>ثمن التذكرة الإجمالي (سعر التذكرة + جميع العمولات) (دج) *</Label>
                <Input
                  type="number"
                  value={ticketForm.ticket_price}
                  onChange={(e) => setTicketForm({...ticketForm, ticket_price: e.target.value})}
                  placeholder="السعر الإجمالي شاملاً كل العمولات"
                />
              </div>
              
              <div>
                <Label>العمولة للوكالات المناولة (دج)</Label>
                <Input
                  type="number"
                  value={ticketForm.commission}
                  onChange={(e) => setTicketForm({...ticketForm, commission: e.target.value})}
                  placeholder="عمولة الوكالة المناولة (اختياري)"
                />
              </div>

              {/* 🆕 Profit display in EDIT dialog too */}
              {(ticketForm.ticket_price || ticketForm.commission) && (
                <div className="md:col-span-2 bg-gradient-to-r from-fuchsia-50 to-purple-100 border-2 border-fuchsia-300 rounded-lg p-3" data-testid="ticket-profit-display-edit">
                  <div className="flex items-center justify-between">
                    <span className="text-fuchsia-900 font-bold text-sm">💎 الربح الصافي للوكالة:</span>
                    <span className="text-2xl font-extrabold text-fuchsia-700">
                      {(() => {
                        const sell = parseFloat(ticketForm.ticket_price || 0);
                        const comm = parseFloat(ticketForm.commission || 0);
                        const cost = parseFloat(ticketForm.supplier_cost || 0);
                        const profit = sell - comm - cost;
                        return profit.toLocaleString() + ' دج';
                      })()}
                    </span>
                  </div>
                  <p className="text-xs text-fuchsia-700 mt-1">
                    💡 المعادلة: الثمن الإجمالي − عمولة الوكالة المناولة − تكلفة المورد
                  </p>
                </div>
              )}
            </div>

            {/* Current Image Display */}
            {editingTicket?.ticket_image_url && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <Label className="text-sm text-gray-700 mb-2 block">الصورة الحالية:</Label>
                <img 
                  src={`${API}${editingTicket.ticket_image_url}`}
                  alt="صورة التذكرة الحالية" 
                  className="w-32 h-32 object-contain rounded border cursor-pointer"
                  onClick={() => window.open(`${API}${editingTicket.ticket_image_url}`, '_blank')}
                />
                <p className="text-xs text-gray-500 mt-1">انقر للتكبير</p>
              </div>
            )}
            
            {/* Warning for editing */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <h4 className="font-bold text-orange-800 mb-1">⚠️ تحذير مهم:</h4>
              <p className="text-sm text-orange-700">
                تعديل التذكرة سيُحدث العملية المرتبطة في العمليات اليومية تلقائياً
              </p>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                إلغاء
              </Button>
              <Button onClick={updateTicket}>
                ✏️ حفظ التعديلات
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📊 تقرير التذاكر</DialogTitle>
            <DialogDescription>
              تقرير شامل للتذاكر مع الإحصائيات والتفاصيل
            </DialogDescription>
          </DialogHeader>
          
          {reportData && (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="bg-brand-50 p-4 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-2">معلومات التقرير:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>المُنشئ:</strong> {reportData.generated_by}</div>
                  <div><strong>تاريخ الإنشاء:</strong> {new Date(reportData.generated_at).toLocaleDateString('ar-DZ')}</div>
                  <div><strong>الفترة:</strong> {reportData.report_period?.start_date || 'غير محدد'} - {reportData.report_period?.end_date || 'غير محدد'}</div>
                  <div><strong>الوكالة:</strong> {reportData.report_period?.agency_filter === 'all' ? 'جميع الوكالات' : 'وكالة محددة'}</div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-brand-600">{reportData.summary?.total_tickets || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي التذاكر</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-green-600">{reportData.summary?.total_price?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">مجموع الأسعار (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-purple-600">{reportData.summary?.total_commission?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">مجموع العمولات (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-orange-600">{reportData.summary?.net_amount?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">صافي المبلغ (دج)</div>
                  </CardContent>
                </Card>
              </div>

              {/* Agency Summary */}
              {Object.keys(reportData.by_agency || {}).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>📊 ملخص الوكالات</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(reportData.by_agency).map(([agencyId, data]) => (
                        <div key={agencyId} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{data.agency_name}</span>
                            <div className="text-right text-sm">
                              <div>{data.tickets_count} تذكرة</div>
                              <div className="text-green-600">{data.total_price?.toLocaleString()} دج</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowReportDialog(false)}>
                  إغلاق
                </Button>
                <Button onClick={printReport} className="bg-brand-600 hover:bg-brand-700">
                  🖨️ طباعة التقرير PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Document Viewing Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>📄 عرض الوثيقة</DialogTitle>
            <DialogDescription>
              {modalImageTitle}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center">
            <div className="max-w-full">
              <img 
                src={modalImageUrl}
                alt={modalImageTitle}
                className="max-w-full max-h-[70vh] object-contain rounded-lg border"
                style={{ minHeight: '200px' }}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setShowDocumentModal(false)}>
              إغلاق
            </Button>
            <Button 
              onClick={() => window.open(modalImageUrl, '_blank')}
              className="bg-brand-600 hover:bg-brand-700"
            >
              🔍 فتح في نافذة جديدة
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Refund Ticket Modal === */}
      <Dialog
        open={!!refundTicket}
        onOpenChange={(o) => { if (!o) setRefundTicket(null); }}
      >
        <DialogContent className="sm:max-w-[560px]" dir="rtl" data-testid="refund-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-orange-700">
              💸 استرجاع التذكرة
            </DialogTitle>
          </DialogHeader>
          {refundTicket && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>🎟️ <strong>التذكرة:</strong> {refundTicket.ticket_no}</div>
                  <div>👤 <strong>العميل:</strong> {refundTicket.client_name}</div>
                </div>
                <div className="flex flex-wrap justify-between gap-2 mt-1">
                  <div>✈️ <strong>الوجهة:</strong> {refundTicket.destination}</div>
                  <div>💰 <strong>سعر التذكرة:</strong> {Number(refundTicket.ticket_price || 0).toLocaleString()} دج</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-xs leading-relaxed text-yellow-900">
                ⚠️ سيتم إنشاء عملية مالية <b>سالبة</b> في "العمليات اليومية"
                لإنقاص المبلغ من الصندوق + تظهر تلقائياً في التقارير.
              </div>

              <div>
                <Label>💰 مبلغ الاسترجاع (دج) *</Label>
                <Input type="number" min="1" step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="المبلغ المُستَرجَع نقداً"
                  data-testid="refund-amount-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  يمكن أن يكون أقل من سعر التذكرة (في حالة خصم رسوم الإلغاء)
                </p>
              </div>

              <div>
                <Label>📝 سبب الاسترجاع *</Label>
                <textarea
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={2}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="مثال: ألغى العميل الرحلة بسبب ظرف صحي"
                  data-testid="refund-reason-input"
                />
              </div>

              <div>
                <Label>📷 صورة الوثيقة (إجبارية) *</Label>
                <Input type="file" accept="image/*" capture="environment"
                  onChange={(e) => setRefundImage(e.target.files?.[0] || null)}
                  data-testid="refund-image-input"
                />
                {refundImage && (
                  <p className="text-xs text-green-700 mt-1">
                    ✅ {refundImage.name} ({(refundImage.size / 1024).toFixed(0)} KB)
                  </p>
                )}
                <p className="text-[11px] text-red-700 mt-1 font-bold">
                  💡 يُقبل JPG/PNG/WebP فقط — <span className="underline">PDF غير مسموح</span> لأنه يُحذف بعد فترة زمنية.
                </p>
              </div>

              <div className="flex justify-between gap-2 pt-3 border-t flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setRefundTicket(null)}
                  data-testid="refund-cancel"
                >
                  ❌ إلغاء
                </Button>
                <Button
                  onClick={submitRefund}
                  disabled={refundBusy || !refundAmount || !refundReason || !refundImage}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
                  data-testid="refund-submit"
                >
                  {refundBusy ? '⏳ جاري المعالجة...' : '✅ تأكيد الاسترجاع'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 🤝 Subagent supplier-picker dialog (opens when approving a ticket
          from a B2B partner agency — choice C, mandatory). */}
      <SubagentApprovalDialog
        open={!!subagentApprovalTarget}
        onClose={() => setSubagentApprovalTarget(null)}
        onConfirm={handleSubagentApprovalConfirm}
        itemLabel={subagentApprovalTarget?.itemLabel}
        agencyName={subagentApprovalTarget?.agencyName}
        sellPrice={subagentApprovalTarget?.sellPrice}
        loading={subagentApprovalBusy}
      />

      {/* 🆕 Grant Credit Note (exceptional discount after full settlement) */}
      {creditNoteFor && (
        <GrantCreditNoteDialog
          bookingType="ticket"
          bookingId={creditNoteFor.id}
          bookingLabel={creditNoteFor.label}
          onClose={() => setCreditNoteFor(null)}
          onCreated={() => { setCreditNoteFor(null); fetchTickets(); }}
        />
      )}
    </div>
  );
});

// ====================================================================================================
// NEW: ORGANIZED TRIPS MANAGEMENT COMPONENT - مكون إدارة الرحلات المنظمة
// ====================================================================================================


export default TicketsManagement;
